import React from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Typography,
    Box,
    Chip,
    Slider,
    Input
} from '@mui/material';

export default function TargetingOptions({ 
    selectedGenders, 
    setSelectedGenders,
    selectedAgeRange,
    setSelectedAgeRange,
    membershipFilter,
    setMembershipFilter,
    includeMembershipQuestion = true
}) {
    const genderOptions = [
        { value: 'all', label: 'All' },
        { value: 'male', label: 'Men' },
        { value: 'female', label: 'Women' },
        { value: 'nonBinary', label: 'Non-binary' },
        { value: 'other', label: 'Other' }
    ];

    // Default age range
    const [ageRange, setAgeRange] = React.useState([21, 99]);

    const handleAgeChange = (event, newValue) => {
        setAgeRange(newValue);
        // Convert to string format for API consistency
        const rangeString = newValue[0] === 21 && newValue[1] === 99 ? 'all' : `${newValue[0]}-${newValue[1]}`;
        setSelectedAgeRange(rangeString);
    };

    const handleGenderChange = (event) => {
        const value = event.target.value;
        
        if (value.includes('all')) {
            if (selectedGenders.includes('all')) {
                setSelectedGenders([]);
            } else {
                setSelectedGenders(['all', ...genderOptions.map(option => option.value)]);
            }
        } else {
            const newSelection = value.filter(v => v !== 'all');
            setSelectedGenders(newSelection);
        }
    };

    const renderGenderValue = (selected) => {
        if (selected.includes('all') || selected.length === 0) {
            return 'All';
        }
        
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                    <Chip 
                        key={value} 
                        label={genderOptions.find(opt => opt.value === value)?.label} 
                        size="small"
                    />
                ))}
            </Box>
        );
    };

    // Initialize age range from selectedAgeRange prop if it exists
    React.useEffect(() => {
        if (selectedAgeRange && selectedAgeRange !== 'all') {
            const [min, max] = selectedAgeRange.split('-').map(Number);
            setAgeRange([min, max || 99]);
        }
    }, []);

    return (
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel className="dialog-input-label">Gender</InputLabel>
                <Select
                    multiple
                    value={selectedGenders}
                    onChange={handleGenderChange}
                    renderValue={renderGenderValue}
                    label="Gender"
                    className="dialog-input"
                >
                    {genderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={selectedGenders.includes(option.value)} />
                            <ListItemText primary={option.label} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2
                }}>
                    <InputLabel 
                        className="dialog-input-label"
                        sx={{ 
                            position: 'relative', 
                            transform: 'none',
                        }}
                    >
                        Age Range: {ageRange[0]} - {ageRange[1]}{ageRange[1] === 99 ? '+' : ''}
                    </InputLabel>
                    <Slider
                        value={ageRange}
                        onChange={handleAgeChange}
                        valueLabelDisplay="auto"
                        min={21}
                        max={99}
                        className="age-range-slider"
                    />
                </Box>
            </FormControl>

            {includeMembershipQuestion && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel className="dialog-input-label">Membership Status</InputLabel>
                    <Select
                        value={membershipFilter}
                        onChange={(e) => setMembershipFilter(e.target.value)}
                        label="Membership Status"
                        className="dialog-input"
                    >
                        <MenuItem value="all">All Users</MenuItem>
                        <MenuItem value="yes">Members Only</MenuItem>
                        <MenuItem value="no">Non-Members Only</MenuItem>
                    </Select>
                </FormControl>
            )}
        </Box>
    );
} 